'use client';

import { cva } from 'class-variance-authority';

import { ThemeColor } from '@/lib/config/colors';
import { useTheme } from '@/lib/utils/themeContext';

export type HeadlineHierarchy = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadlinePosition = 'left' | 'center' | 'right';

type HeadlineProps = {
  hierarchy?: HeadlineHierarchy;
  position?: HeadlinePosition;
  size?: HeadlineHierarchy;
  color?: ThemeColor | 'white' | 'grey';
  className?: string;
  children: React.ReactNode;
};

const sizes = {
  1: 'text-4xl md:text-6xl mb-4 md:mb-8 leading-none',
  2: 'text-3xl md:text-5xl mb-3 leading-none',
  3: 'text-xl md:text-3xl mb-2 leading-tight',
  4: 'text-lg md:text-2xl leading-tight',
  5: 'text-lg md:text-xl leading-tight',
  6: 'text-lg leading-tight',
};

const headlineClasses = cva('font-corporate font-bold', {
  variants: {
    hierarchy: {
      1: null,
      2: null,
      3: null,
      4: null,
      5: null,
      6: null,
    },
    position: {
      left: null,
      center: 'text-center',
      right: 'text-right',
    },
    size: sizes,
    color: {
      default: 'text-purple-600',
      purple: 'text-purple-600',
      blue: 'text-blue-600',
      green: 'text-green-600',
      orange: 'text-orange-600',
      red: 'text-red-600',
      teal: 'text-teal-600',
      white: 'text-white',
      grey: 'text-grey-600',
    },
  },
  compoundVariants: Object.entries(sizes).map(([key, value]) => ({
    hierarchy: parseInt(key) as HeadlineHierarchy,
    size: null,
    className: value,
  })),
  defaultVariants: {
    hierarchy: 1,
    position: 'left',
    size: null,
    color: null,
  },
});

const Headline = ({ hierarchy = 1, position, size, color, className, children }: HeadlineProps) => {
  const theme = useTheme();
  const Component = `h${hierarchy}` as keyof JSX.IntrinsicElements;

  return (
    <Component className={headlineClasses({ hierarchy, position, size, color: color || theme.color, className })}>
      {children}
    </Component>
  );
};

export default Headline;
